import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with defaults
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add these options to handle CORS properly
  withCredentials: false,
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Log the token being sent (first few characters only for security)
      console.log(`Request to ${config.url} - Adding token: ${token.substring(0, 10)}...`);
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.log(`Request to ${config.url} - No token available`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to log errors
api.interceptors.response.use(
  (response) => {
    console.log(`Response from ${response.config.url} - Status: ${response.status}`);
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.config?.headers
    });
    return Promise.reject(error);
  }
);

// Special direct Axios call with full token in header for debugging
export const debugCheckAuth = async (token) => {
  try {
    console.log('Debug Check Auth - Full token being sent for debugging purposes');
    const response = await axios.get(`${BASE_URL}/user/me`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('Debug Auth Response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Debug Auth Error:', {
      status: err.response?.status,
      data: err.response?.data,
      headers: err.response?.headers
    });
    throw err;
  }
};

// Auth API
export const fetchUserProfile = async (token) => {
  console.log('Fetching user profile with token:', token ? `${token.substring(0, 10)}...` : 'none');
  try {
    // Log the base URL being used
    console.log('Base URL for API calls:', BASE_URL);
    
    // Method 1: Using the api instance with interceptor
    console.log('Attempting to fetch user profile with API instance');
    const response = await api.get('/user/me', {
      headers: { 
        Authorization: `Bearer ${token}`,
        // Try adding a custom header to bypass any caching
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    console.log('User profile response status:', response.status);
    console.log('User profile response full data:', response.data);
    
    // Get the profile data from the response
    const profileData = response.data.data;
    
    // Log available keys to help with debugging
    console.log('Available profile keys:', Object.keys(profileData));
    
    // Handle the new format where teams are in an array of objects
    if (Array.isArray(profileData.teams) && profileData.teams.length > 0) {
      console.log('Detected array of teams. Using the first team:', profileData.teams[0]);
      
      // Create a new object with the first team's ID and name
      const processedData = {
        ...profileData,
        teamId: profileData.teams[0].teamId,
        teamName: profileData.teams[0].teamName
      };
      
      return processedData;
    } else if (!profileData.teamId) {
      console.warn('WARNING: No teamId in user profile response!');
      console.warn('This might be why team-related operations are failing');
      console.warn('Full profile data:', profileData);
    }
    
    return profileData;
  } catch (err) {
    // If it fails with api instance, try direct axios call
    console.error('Error fetching user profile:', err.response?.data || err.message);
    console.error('Full error details:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      headers: err.response?.headers,
    });
    
    // Try Method 2 with a direct axios call
    try {
      console.log('Retrying with direct axios call');
      const directResponse = await axios.get(`${BASE_URL}/user/me`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Direct axios call succeeded:', directResponse.data);
      
      // Get the profile data from the direct response
      const profileData = directResponse.data.data;
      
      // Log available keys to help with debugging
      console.log('Available profile keys (direct):', Object.keys(profileData));
      
      // Handle the new format where teams are in an array of objects
      if (Array.isArray(profileData.teams) && profileData.teams.length > 0) {
        console.log('Detected array of teams in direct call. Using the first team:', profileData.teams[0]);
        
        // Create a new object with the first team's ID and name
        const processedData = {
          ...profileData,
          teamId: profileData.teams[0].teamId,
          teamName: profileData.teams[0].teamName
        };
        
        return processedData;
      } else if (!profileData.teamId) {
        console.warn('WARNING: No teamId in direct user profile response!');
        console.warn('This might be why team-related operations are failing');
        console.warn('Full profile data:', profileData);
      }
      
      return profileData;
    } catch (directErr) {
      console.error('Direct axios call also failed:', directErr.response?.data || directErr.message);
      console.error('Full direct error details:', {
        status: directErr.response?.status,
        statusText: directErr.response?.statusText,
        data: directErr.response?.data,
        headers: directErr.response?.headers,
      });
      throw err; // Throw the original error
    }
  }
};

// Matter API 
export const fetchmatters = async (teamId, status = null) => {
  try {
    let url = `/teams/${teamId}/matters`;
    if (status) {
      url += `?status=${status}`;
    }
    
    console.log(`Fetching matters for team ${teamId}${status ? ` with status ${status}` : ''}`);
    const response = await api.get(url);
    console.log('Matter list API response:', response.data);
    
    // Based on the provided response format:
    // {
    //   "success": true,
    //   "message": "Found X matters",
    //   "data": {
    //     "matters": [ ... array of matters ... ],
    //     "total_count": X,
    //     "last_id": "...",
    //     "has_more": true/false
    //   }
    // }
    
    if (response.data && response.data.data && response.data.data.matters) {
      console.log(`Found ${response.data.data.matters.length} matters`);
      
      // Map backend response fields to what the frontend expects
      // 'matterstatus' in backend maps to 'status' in frontend
      const mappedmatters = response.data.data.matters.map(caseItem => ({
        ...caseItem,
        status: caseItem.matterstatus, // Map matterstatus to status for frontend compatibility
        isDeleted: caseItem.isDeleted, // Map isDeleted to is_deleted for frontend compatibility
        fileCount: caseItem.fileCount || caseItem.file_count || 0, // Handle both fileCount and file_count
        chatCount: caseItem.conversationCount || caseItem.chat_count || 0 // Handle both conversationCount and chat_count
      }));
      
      return {
        items: mappedmatters,
        total: response.data.data.totalCount,
        hasMore: response.data.data.hasMore
      };
    }
    
    console.warn('Unexpected matter data structure:', response.data);
    return { items: [], total: 0, hasMore: false };
  } catch (error) {
    console.error('Error in fetchmatters API call:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const deleteMatter = async (teamId, matterId) => {
  try {
    console.log(`Deleting matter ${matterId} for team ${teamId}`);
    const response = await api.delete(`/teams/${teamId}/matters/${matterId}`);
    console.log('Delete matter API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in deleteMatter API call:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const fetchCase = async (teamId, caseId) => {
  try {
    console.log(`Fetching matter ${caseId} for team ${teamId}`);
    const response = await api.get(`/teams/${teamId}/matters/${caseId}`);
    console.log('Matter detail API response:', response.data);
    
    // Check if the response has the expected structure
    if (response.data && response.data.data) {
      let caseData = response.data.data;
      
      // If the matter is nested in a 'matter' property
      if (caseData.matter) {
        caseData = caseData.matter;
      }
      
      // Map backend fields to what the frontend expects
      return {
        ...caseData,
        status: caseData.matterstatus, // Map matterstatus to status for frontend compatibility
        isDeleted: caseData.isDeleted, // Map isDeleted to is_deleted for frontend compatibility
        fileCount: caseData.fileCount || caseData.file_count || 0, // Handle both fileCount and file_count
        chatCount: caseData.conversationCount || caseData.chat_count || 0 // Handle both conversationCount and chat_count
      };
    }
    
    console.warn('Unexpected matter detail structure:', response.data);
    return response.data.data; // fallback to original behavior
  } catch (error) {
    console.error('Error in fetchCase API call:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const createCase = async (teamId, caseData) => {
  try {
    console.log(`Creating matter for team ${teamId}`, caseData);
    
    // If the frontend is sending "status" but backend expects "matterstatus"
    const adaptedCaseData = {
      ...caseData,
      matterstatus: caseData.status || caseData.matterstatus || 'New'
    };
    
    // Remove any fields the backend doesn't expect
    if (adaptedCaseData.status) {
      delete adaptedCaseData.status;
    }
    
    const response = await api.post(`/teams/${teamId}/matters`, adaptedCaseData);
    console.log('Create matter API response:', response.data);
    
    // Handle the response 
    if (response.data && response.data.data) {
      let newCase = response.data.data;
      
      // If the matter is nested in a 'matter' property
      if (newCase.matter) {
        newCase = newCase.matter;
      }
      
      // Map backend fields to what the frontend expects
      return {
        ...newCase,
        status: newCase.matterstatus, // Map matterstatus to status for frontend compatibility
        isDeleted: newCase.isDeleted, // Map isDeleted to is_deleted for frontend compatibility
        fileCount: newCase.fileCount || newCase.file_count || 0, // Handle both fileCount and file_count
        chatCount: newCase.conversationCount || newCase.chat_count || 0 // Handle both conversationCount and chat_count
      };
    }
    
    return response.data.data; // fallback to original behavior
  } catch (error) {
    console.error('Error in createCase API call:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const updatematterstatus = async (teamId, caseId, status) => {
  const response = await api.patch(`/teams/${teamId}/matters/${caseId}/status`, { status });
  return response.data.data;
};

// Documents API
export const fetchCaseFiles = async (teamId, caseId, limit = 10, includeDeleted = false, lastId = null) => {
  // Build query parameters
  let queryParams = `limit=${limit}&include_deleted=${includeDeleted}`;
  
  // Add lastId for pagination if provided
  if (lastId) {
    queryParams += `&last_id=${lastId}`;
  }
  
  const response = await api.get(
    `/teams/${teamId}/matters/${caseId}/documents?${queryParams}`
  );
  
  // Return standardized response format for the frontend
  // If the backend response follows the format you provided, this ensures consistent data structure
  if (response.data && response.data.data && response.data.data.files) {
    return {
      items: response.data.data.files,
      totalCount: response.data.data.total_count,
      lastId: response.data.data.last_id,
      hasMore: response.data.data.has_more
    };
  }
  
  // Fallback to original behavior if response format is different
  return response.data.data;
};

export const uploadFile = async (teamId, caseId, file, metadata = {}) => {
  try {
    console.log(`Uploading file to team ${teamId}, matter ${caseId}`);
    console.log('File to upload:', file.name, file.size, file.type);
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Add metadata fields to the form data
    if (metadata.filename) {
      console.log('Custom filename:', metadata.filename);
      formData.append('filename', metadata.filename);
    }
    
    if (metadata.description) {
      console.log('File description:', metadata.description);
      formData.append('description', metadata.description);
    }
    
    if (metadata.tags && metadata.tags.length > 0) {
      console.log('File tags:', metadata.tags);
      formData.append('tags', metadata.tags.join(','));
    }
    
    const response = await api.post(
      `/teams/${teamId}/matters/${caseId}/documents`, 
      formData, 
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    console.log('File upload response:', response.data);
    return response.data.data;
  } catch (error) {
    console.error('Error uploading file:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

// Update file metadata based on schema
export const updateFileMetadata = async (teamId, caseId, fileId, metadata = {}) => {
  try {
    console.log(`Updating file ${fileId} metadata for team ${teamId}, matter ${caseId}`);
    
    // Create payload based on FileMetadataUpdateRequest schema
    const payload = {};
    
    if (metadata.filename !== undefined) {
      payload.filename = metadata.filename;
    }
    
    if (metadata.description !== undefined) {
      payload.description = metadata.description;
    }
    
    if (metadata.tags !== undefined) {
      payload.tags = metadata.tags;
    }
    
    console.log('Metadata update payload:', payload);
    
    const response = await api.patch(
      `/teams/${teamId}/matters/${caseId}/documents/${fileId}`,
      payload
    );
    
    console.log('Metadata update response:', response.data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating file metadata:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

// Conversations API
export const fetchConversations = async (teamId, caseId) => {
  try {
    console.log(`Fetching conversations for team ${teamId}, matter ${caseId}`);
    const response = await api.get(`/teams/${teamId}/matters/${caseId}/conversations`);
    console.log('Conversations response:', response.data);
    
    // Based on the updated OpenAPI schema:
    // {
    //   "success": true,
    //   "message": "Found X conversations",
    //   "data": {
    //     "conversations": [ ... array of conversations ... ],
    //     "totalCount": X,
    //     "lastId": "..."
    //   }
    // }
    
    if (response.data && response.data.data && response.data.data.conversations) {
      console.log(`Found ${response.data.data.conversations.length} conversations`);
      
      return {
        items: response.data.data.conversations,
        total: response.data.data.totalCount,
        lastId: response.data.data.lastId
      };
    }
    
    // Fallback checks
    if (response.data && response.data.data) {
      if (Array.isArray(response.data.data)) {
        console.log('Data is directly an array, returning as items');
        return { items: response.data.data };
      }
      
      // Check for other possible keys
      const commonArrayKeys = ['conversations', 'items', 'results', 'list'];
      for (const key of commonArrayKeys) {
        if (response.data.data[key] && Array.isArray(response.data.data[key])) {
          console.log(`Found array in data.${key}`);
          return { items: response.data.data[key] };
        }
      }
    }
    
    console.warn('Could not find conversations array in the response:', response.data);
    return { items: [] };
  } catch (error) {
    console.error('Error fetching conversations:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const createConversation = async (teamId, caseId, title, fileIds = [], initialMessage = "", model = "claude-3-5-sonnet", temperature = 0.7) => {
  try {
    console.log(`Creating conversation for team ${teamId}, matter ${caseId}`);
    
    // Updated payload structure based on OpenAPI spec
    const payload = { 
      title
    };
    
    // Include initialMessage as userPrompt if provided
    if (initialMessage) {
      payload.userPrompt = initialMessage;
    }
    
    // Add temperature setting
    payload.temperature = temperature;
    
    // Only add model if provided and not empty
    if (model && model.trim() !== '') {
      payload.model = model;
    }
    
    // Add fileIds only if there are files to include
    if (fileIds && fileIds.length > 0) {
      payload.fileIds = fileIds;
    }
    
    console.log('Creating conversation with payload:', payload);
    
    const response = await api.post(`/teams/${teamId}/matters/${caseId}/conversations`, payload);
    console.log('Create conversation response:', response.data);
    
    return response.data.data;
  } catch (error) {
    console.error('Error creating conversation:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const fetchConversation = async (teamId, caseId, chatId) => {
  try {
    console.log(`Fetching conversation ${chatId} for team ${teamId}, matter ${caseId}`);
    const response = await api.get(`/teams/${teamId}/matters/${caseId}/conversations/${chatId}`);
    console.log('Conversation detail response:', response.data);
    
    // Expected structure from OpenAPI spec:
    // {
    //   "success": true,
    //   "message": "Conversation details",
    //   "data": {
    //     "id": "...",
    //     "title": "...",
    //     "messages": [...],
    //     "createdAt": "...",
    //     "updatedAt": "...",
    //     "fileIds": [...],
    //     "filePaths": [...],
    //     "model": "..."
    //   }
    // }
    
    if (response.data && response.data.data) {
      return response.data.data;
    }
    
    console.warn('Unexpected conversation response structure:', response.data);
    return response.data.data; // Fallback
  } catch (error) {
    console.error('Error fetching conversation:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const sendMessage = async (teamId, caseId, chatId, message, temperature = 0.7, model = "claude-3-5-sonnet") => {
  try {
    console.log(`Sending message to conversation ${chatId} in matter ${caseId} for team ${teamId}`);
    
    // Updated payload structure based on OpenAPI spec
    const payload = {
      message: message,
      temperature: temperature,
      model: model
    };
    
    console.log('Sending message with payload:', payload);
    
    const response = await api.post(
      `/teams/${teamId}/matters/${caseId}/conversations/${chatId}/messages`, 
      payload
    );
    console.log('Send message response:', response.data);
    
    return response.data.data;
  } catch (error) {
    console.error('Error sending message:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const deleteConversation = async (teamId, caseId, conversationId) => {
  try {
    console.log(`Deleting conversation ${conversationId} from matter ${caseId} for team ${teamId}`);
    
    const response = await api.delete(
      `/teams/${teamId}/matters/${caseId}/conversations/${conversationId}`
    );
    console.log('Delete conversation response:', response.data);
    
    return response.data.data;
  } catch (error) {
    console.error('Error deleting conversation:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

// Prompts API
export const fetchPrompts = async (teamId) => {
  try {
    console.log(`Fetching prompts for team ${teamId}`);
    const response = await api.get(`/teams/${teamId}/prompts`);
    console.log('Prompt list API response:', response.data);
    
    // Based on the provided response format:
    // {
    //   "success": true,
    //   "message": "Found X prompts",
    //   "data": {
    //     "prompts": [ ... array of prompts ... ],
    //     "count": X
    //   }
    // }
    
    if (response.data && 
        response.data.data && 
        response.data.data.prompts && 
        Array.isArray(response.data.data.prompts)) {
      // This is the correct path based on the API response you provided
      console.log('Found prompts array at response.data.data.prompts');
      return response.data.data.prompts;
    }
    
    // Fallback checks for other possible structures
    if (response.data && response.data.data) {
      const data = response.data.data;
      
      // Check if data itself is an array
      if (Array.isArray(data)) {
        console.log('Data is directly an array');
        return data;
      }
      
      // Check common property names for arrays
      const arrayProperties = ['items', 'prompts', 'results', 'list'];
      for (const prop of arrayProperties) {
        if (data[prop] && Array.isArray(data[prop])) {
          console.log(`Found array in response.data.data.${prop}`);
          return data[prop];
        }
      }
    }
    
    // Final fallback - return empty array
    console.warn('Could not find prompts array in response:', response.data);
    return [];
  } catch (error) {
    console.error('Error in fetchPrompts API call:');
    console.error('- Status:', error.response?.status);
    console.error('- Status text:', error.response?.statusText);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const fetchPrompt = async (teamId, promptId) => {
  const response = await api.get(`/teams/${teamId}/prompts/${promptId}`);
  return response.data.data;
};

export const createPrompt = async (teamId, promptData) => {
  try {
    console.log(`Creating prompt for team ${teamId} with data:`, promptData);
    console.log('API URL being called:', `${BASE_URL}/teams/${teamId}/prompts`);
    
    const response = await api.post(`/teams/${teamId}/prompts`, promptData);
    
    console.log('Prompt creation API response data:', response.data);
    
    // Check the response structure based on the API response format
    // For create operations, the response might return just the created prompt object directly
    if (response.data && response.data.data) {
      // If there's a prompt object directly in data
      if (response.data.data.id) {
        console.log('  → Returning single prompt from response.data.data');
        return response.data.data;
      }
      
      // If it's in data.prompt or data.prompts (first item)
      if (response.data.data.prompt) {
        console.log('  → Returning from response.data.data.prompt');
        return response.data.data.prompt;
      }
      
      if (response.data.data.prompts && Array.isArray(response.data.data.prompts) && response.data.data.prompts.length > 0) {
        console.log('  → Returning first item from response.data.data.prompts');
        return response.data.data.prompts[0];
      }
      
      // If it's just a nested object with unknown structure, return as is
      console.log('  → Returning response.data.data (structure unknown)');
      return response.data.data;
    } else if (response.data) {
      // Direct response.data might contain the prompt object
      if (response.data.id) {
        console.log('  → Returning prompt directly from response.data');
        return response.data; 
      }
      
      console.log('  → Returning full response.data (structure unknown)');
      return response.data;
    } else {
      console.warn('  → No data in response, returning empty object');
      return {};
    }
  } catch (error) {
    console.error('Error in createPrompt API call:');
    console.error('- Status:', error.response?.status);
    console.error('- Status text:', error.response?.statusText);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error; // Re-throw the error for the component to handle
  }
};

export const updatePrompt = async (teamId, promptId, promptData) => {
  const response = await api.put(`/teams/${teamId}/prompts/${promptId}`, promptData);
  return response.data.data;
};

export const deletePrompt = async (teamId, promptId) => {
  const response = await api.delete(`/teams/${teamId}/prompts/${promptId}`);
  return response.data.data;
};

// Team API
export const fetchTeam = async (teamId) => {
  try {
    console.log(`Fetching team details for team ${teamId}`);
    const response = await api.get(`/teams/${teamId}`);
    console.log('Team details API response:', response.data);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching team details:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export const updateTeam = async (teamId, updateData) => {
  try {
    console.log(`Updating team ${teamId} with data:`, updateData);
    const response = await api.patch(`/teams/${teamId}`, updateData);
    console.log('Team update API response:', response.data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating team:');
    console.error('- Status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Error message:', error.message);
    throw error;
  }
};

export default api;